import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { API, api, fmtErr, fmtDate, fmtDT, fmtQty, fileUrl } from "../api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { Printer, Camera, Upload } from "lucide-react";

export default function LotDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [lot, setLot] = useState(null);
  const [qc, setQc] = useState({ joints: "", box_weight: "", width: "", zipper_weight: "", status: "Pending", remarks: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const canQC = ["admin", "production"].includes(user?.role);

  const load = useCallback(() => {
    api.get(`/lots/${id}`).then((r) => {
      setLot(r.data);
      const q = r.data.qc || {};
      setQc({
        joints: q.joints ?? "", box_weight: q.box_weight ?? "", width: q.width ?? "",
        zipper_weight: q.zipper_weight ?? "", status: q.status || "Pending", remarks: q.remarks || "",
      });
    }).catch((e) => toast.error(fmtErr(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!lot) return <div className="text-slate-500" data-testid="lot-loading">Loading lot...</div>;

  const saveQC = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/lots/${id}/qc`, {
        joints: qc.joints === "" ? null : parseFloat(qc.joints),
        box_weight: qc.box_weight === "" ? null : parseFloat(qc.box_weight),
        width: qc.width === "" ? null : qc.width,
        zipper_weight: qc.zipper_weight === "" ? null : parseFloat(qc.zipper_weight),
        status: qc.status, remarks: qc.remarks,
      });
      toast.success("QC saved");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/lots/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="lot-detail-page" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-extrabold font-mono text-[#002B5D] tracking-tight" data-testid="lot-number">{lot.lot_number}</h1>
            <StatusBadge status={lot.qc?.status || "Pending"} testid="lot-qc-status" />
          </div>
          <p className="text-slate-500 mt-1">{lot.product_name} <span className="font-mono text-xs">({lot.product_code})</span></p>
        </div>
        <a href={`${API}/labels.pdf?lot_id=${id}`} download data-testid="print-qr-label-button"
          className="h-11 px-5 rounded-md font-heading font-bold text-sm text-white inline-flex items-center gap-2" style={{ backgroundColor: "#002B5D" }}>
          <Printer className="w-4 h-4" /> Download QR Labels PDF
        </a>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="sk-card p-5 space-y-3" data-testid="lot-info">
          <h3 className="font-heading font-bold text-[#002B5D]">Lot Information</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-slate-500">Product</dt><dd>{lot.product_name}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Product Code</dt><dd className="font-mono">{lot.product_code}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Manufacturing Order</dt><dd><Link to={`/manufacturing/orders/${lot.mo_id}`} className="font-mono text-[#EB1C27] hover:underline">{lot.mo_number}</Link></dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">BOM</dt><dd>{lot.bom_name || "-"} {lot.bom_version ? `v${lot.bom_version}` : ""}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Production Date</dt><dd>{fmtDate(lot.production_date)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">MO Status</dt><dd>{lot.mo_status ? <StatusBadge status={lot.mo_status} /> : "-"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Started</dt><dd className="font-mono text-xs">{fmtDT(lot.started_at)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Completed</dt><dd className="font-mono text-xs">{fmtDT(lot.completed_at)}</dd></div>
          </dl>
        </div>

        <div className="sk-card p-5 space-y-3" data-testid="lot-balance">
          <h3 className="font-heading font-bold text-[#002B5D]">Lot Inventory Balance</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-slate-500">Produced</dt><dd className="font-mono font-semibold" data-testid="lot-produced">{fmtQty(lot.produced_qty)} {lot.uom}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Deducted</dt><dd className="font-mono font-semibold text-rose-600" data-testid="lot-deducted">{fmtQty(lot.deducted_qty)} {lot.uom}</dd></div>
            <div className="flex justify-between border-t border-border pt-2"><dt className="font-semibold">Available</dt><dd className="font-mono font-bold text-green-700 text-base" data-testid="lot-available">{fmtQty(lot.current_qty)} {lot.uom}</dd></div>
          </dl>
          <div className="pt-2">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">QR Code</div>
            <img src={`${API}/lots/${id}/qr`} alt={`QR ${lot.lot_number}`} className="w-32 h-32 border border-border rounded" data-testid="lot-qr-image" />
            <p className="text-[10px] text-slate-400 mt-1">Scan to open this lot</p>
          </div>
        </div>

        {(!lot.boxes || lot.boxes.length === 0) && (
        <div className="sk-card p-5" data-testid="lot-qc-panel">
          <h3 className="font-heading font-bold text-[#002B5D] mb-3">QC Information</h3>
          <form onSubmit={saveQC} className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="sk-label">No. of Joints</label>
                <input data-testid="qc-joints-input" type="number" step="any" disabled={!canQC} className="sk-input" value={qc.joints} onChange={(e) => setQc({ ...qc, joints: e.target.value })} />
              </div>
              <div>
                <label className="sk-label">Box Weight</label>
                <input data-testid="qc-box-weight-input" type="number" step="any" disabled={!canQC} className="sk-input" value={qc.box_weight} onChange={(e) => setQc({ ...qc, box_weight: e.target.value })} />
              </div>
              <div>
                <label className="sk-label">Width of Zipper</label>
                <input data-testid="qc-width-input" disabled={!canQC} className="sk-input" placeholder="e.g. 10 mm" value={qc.width} onChange={(e) => setQc({ ...qc, width: e.target.value })} />
              </div>
              <div>
                <label className="sk-label">Weight of Zipper</label>
                <input data-testid="qc-zipper-weight-input" type="number" step="any" disabled={!canQC} className="sk-input" value={qc.zipper_weight} onChange={(e) => setQc({ ...qc, zipper_weight: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="sk-label">QC Status</label>
              <select data-testid="qc-status-select" disabled={!canQC} className="sk-input" value={qc.status} onChange={(e) => setQc({ ...qc, status: e.target.value })}>
                {["Pending", "Passed", "Failed", "Hold"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="sk-label">QC Remarks</label>
              <textarea data-testid="qc-remarks-input" disabled={!canQC} className="sk-input h-16 py-2" value={qc.remarks} onChange={(e) => setQc({ ...qc, remarks: e.target.value })} />
            </div>
            {canQC && (
              <button data-testid="qc-save-button" disabled={busy} className="w-full h-11 rounded-md font-heading font-bold text-white disabled:opacity-60" style={{ backgroundColor: "#002B5D" }}>
                {busy ? "Saving..." : "Save QC"}
              </button>
            )}
          </form>

          <div className="mt-4">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">Lock / Seal Photo</div>
            {lot.qc?.photo ? (
              <img src={fileUrl(lot.qc.photo)} alt="Lock / seal" className="w-full rounded border border-border" data-testid="lot-photo" />
            ) : (
              <div className="border border-dashed border-border rounded-md px-4 py-6 text-center text-xs text-slate-400" data-testid="lot-no-photo">No photo uploaded</div>
            )}
            {canQC && (
              <>
                <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                  data-testid="lot-photo-input" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                <button data-testid="lot-photo-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="mt-2 w-full h-11 rounded-md font-heading font-bold text-sm border border-[#002B5D] text-[#002B5D] inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {uploading ? <Upload className="w-4 h-4 animate-pulse" /> : <Camera className="w-4 h-4" />}
                  {uploading ? "Uploading..." : "Upload / Take Photo"}
                </button>
              </>
            )}
            {lot.qc?.updated_by && <p className="text-[10px] text-slate-400 mt-2">QC by {lot.qc.updated_by} · {fmtDT(lot.qc.updated_at)}</p>}
          </div>
        </div>
        )}
      </div>

      {lot.boxes?.length > 0 && (
        <div className="sk-card" data-testid="lot-boxes">
          <h3 className="font-heading font-bold text-[#002B5D] px-5 py-4 border-b border-border">Boxes</h3>
          <table className="w-full sk-table">
            <thead><tr><th>Box</th><th className="text-right">Qty</th><th className="text-right">Available</th><th>Status</th><th>QC</th><th className="text-right">Net Wt</th><th className="text-right">Joints</th><th className="text-right">Time</th><th></th></tr></thead>
            <tbody>
              {lot.boxes.map((b) => (
                <tr key={b.id} data-testid={`lot-box-row-${b.box_number}`}>
                  <td><Link to={`/boxes/${b.id}`} className="font-mono font-semibold text-[#002B5D] hover:underline">{b.box_number}</Link></td>
                  <td className="text-right font-mono">{fmtQty(b.qty)} {b.uom}</td>
                  <td className="text-right font-mono text-green-700 font-semibold">{fmtQty(b.remaining_qty)}</td>
                  <td><StatusBadge status={b.status} /></td>
                  <td>{b.status === "Completed" ? <StatusBadge status={b.qc?.status || "Passed"} /> : <span className="text-xs text-slate-400">{b.qc_missing?.length ? `Missing ${b.qc_missing.length}` : "Ready"}</span>}</td>
                  <td className="text-right font-mono">{b.qc?.box_weight ?? "-"}</td>
                  <td className="text-right font-mono">{b.qc?.joints ?? "-"}</td>
                  <td className="text-right font-mono text-xs">{b.duration_minutes != null ? `${Math.floor(b.duration_minutes / 60)}h ${Math.round(b.duration_minutes % 60)}m` : "-"}</td>
                  <td><Link to={`/boxes/${b.id}/label`} className="text-xs font-semibold text-[#EB1C27] hover:underline">QR Label</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="sk-card" data-testid="lot-movements">
        <h3 className="font-heading font-bold text-[#002B5D] px-5 py-4 border-b border-border">Lot Movements</h3>
        <table className="w-full sk-table">
          <thead><tr><th>Date</th><th>Type</th><th className="text-right">Qty</th><th>Reference</th><th>User</th></tr></thead>
          <tbody>
            {(lot.movements || []).length === 0 && <tr><td colSpan={5} className="text-center text-slate-400">No movements yet</td></tr>}
            {(lot.movements || []).map((m) => (
              <tr key={m.id}>
                <td className="text-slate-500">{fmtDT(m.date)}</td>
                <td>{m.type}</td>
                <td className={`text-right font-mono font-semibold ${m.qty < 0 ? "text-rose-600" : "text-green-700"}`}>{m.qty > 0 ? "+" : ""}{fmtQty(m.qty)}</td>
                <td className="font-mono text-xs">{m.reference}</td>
                <td className="text-slate-500 text-xs">{m.user}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
